import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getDifficultyOptions } from '../data/wordService';
import './Selector.css';

function DifficultySelector({ selectedDifficulties, onDifficultyChange }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectorRef = useRef(null);
  const difficulties = getDifficultyOptions();

  const handleDifficultyToggle = (difficultyValue) => {
    const newSelection = selectedDifficulties.includes(difficultyValue)
      ? selectedDifficulties.filter(diff => diff !== difficultyValue)
      : [...selectedDifficulties, difficultyValue];

    onDifficultyChange(newSelection);
  };

  const getDisplayText = () => {
    if (selectedDifficulties.length === 0) return 'No difficulties selected';
    if (selectedDifficulties.length === difficulties.length) return 'All Difficulties';
    if (selectedDifficulties.length === 1) {
      const difficulty = difficulties.find(diff => diff.value === selectedDifficulties[0]);
      return difficulty?.label || 'Unknown';
    }
    return `${selectedDifficulties.length} Difficulties`;
  };

  const handleToggleExpanded = () => {
    if (isExpanded) {
      // Closing - check if nothing is selected and default to easy
      if (selectedDifficulties.length === 0) {
        onDifficultyChange(['easy']);
      }
    }
    setIsExpanded(!isExpanded);
  };

  const closeDropdown = useCallback(() => {
    if (isExpanded) {
      // Check if nothing is selected and default to easy
      if (selectedDifficulties.length === 0) {
        onDifficultyChange(['easy']);
      }
      setIsExpanded(false);
    }
  }, [isExpanded, selectedDifficulties, onDifficultyChange]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded, closeDropdown]);

  return (
    <div className="selector">
      <label className="selector-label">Difficulties:</label>

      <div className="category-selector" ref={selectorRef}>
        <button
          className="category-toggle-btn"
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
        >
          {getDisplayText()}
          <span className={`arrow ${isExpanded ? 'up' : 'down'}`}>▼</span>
        </button>

        {isExpanded && (
          <div className="category-dropdown">
            <div className="category-options">
              {difficulties.map((difficulty) => (
                <label key={difficulty.value} className="category-option">
                  <input
                    type="checkbox"
                    checked={selectedDifficulties.includes(difficulty.value)}
                    onChange={() => handleDifficultyToggle(difficulty.value)}
                    className="category-checkbox"
                  />
                  <span className="category-label-text">{difficulty.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DifficultySelector;